import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CornerDownLeft,
  FileImage,
  FolderKanban,
  Layers,
  Music2,
  Search,
  Video as VideoIcon,
  X,
} from "lucide-react";
import { useSearch } from "@/hooks/use-search";
import { useProjects } from "@/hooks/use-projects";
import { formatDurationChip } from "@/components/projects/video-card";
import type { SearchScope } from "@/components/layout/search-overlay-context";
import type { Project, Video } from "@/lib/api-types";

type Props = {
  open: boolean;
  onClose: () => void;
  scope: SearchScope;
  /** "/app" or "/admin" — used to route clicks + link to the /search page. */
  basePath: "/app" | "/admin";
};

/**
 * Frame.io-style desktop search overlay. Same UI shell for every trigger
 * (top-bar pill, sidebar rail, in-project button, in-folder button); the
 * `scope` prop controls what actually gets searched. A chip above the input
 * tells the user where they are.
 */
export function SearchOverlay({ open, onClose, scope, basePath }: Props) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const { projects, videos, isLoading } = useSearch(query, { enabled: open, scope });
  const { data: allProjects } = useProjects(open);

  const projectNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of allProjects ?? []) map.set(p.id, p.name);
    return map;
  }, [allProjects]);

  const projectsBasePath = `${basePath}/projects`;
  const watchBasePath = `${basePath}/watch`;
  const searchHref = `${basePath}/search`;

  // Auto-focus + lock scroll while open. Reset query when the overlay closes
  // OR when the scope changes (different scope = different mental context).
  useEffect(() => {
    if (!open) return;
    const raf = window.requestAnimationFrame(() => inputRef.current?.focus());
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.cancelAnimationFrame(raf);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  useEffect(() => {
    // Clear query when scope changes mid-open (defensive — normally the
    // overlay closes and reopens, but a scope swap during an open state
    // should also reset).
    setQuery("");
  }, [scope.kind, scope.kind === "project" ? scope.projectId : scope.kind === "folder" ? scope.folderId : ""]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const hasQuery = query.trim().length > 0;
  const totalCount = projects.length + videos.length;
  const noResults = hasQuery && !isLoading && totalCount === 0;
  const scopeLabel =
    scope.kind === "workspace"
      ? "Workspace"
      : scope.kind === "project"
      ? scope.label || "This project"
      : scope.label || "This folder";
  const scopeSubject =
    scope.kind === "workspace"
      ? "workspace"
      : scope.kind === "project"
      ? "this project"
      : "this folder";

  function goToProject(id: string) {
    navigate(`${projectsBasePath}/${id}`);
    onClose();
  }
  function goToVideo(id: string) {
    navigate(`${watchBasePath}/${id}`);
    onClose();
  }
  function goToSearchPage() {
    // Only meaningful for workspace scope — the /search page is workspace-wide.
    if (scope.kind !== "workspace") return;
    navigate(`${searchHref}?q=${encodeURIComponent(query)}`);
    onClose();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      if (projects.length > 0) goToProject(projects[0].id);
      else if (videos.length > 0) goToVideo(videos[0].id);
      else if (hasQuery && scope.kind === "workspace") goToSearchPage();
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 hidden md:block"
      role="dialog"
      aria-modal="true"
      aria-label="Search"
    >
      <button
        type="button"
        aria-label="Close search"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px] animate-in fade-in duration-150"
      />

      <div
        className="relative mx-auto mt-24 flex w-[92%] max-w-2xl flex-col overflow-hidden rounded-2xl border border-border bg-popover shadow-2xl animate-in fade-in slide-in-from-top-4 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Scope chip — only shown when not workspace-scoped. Makes it obvious
            you're searching a subset. */}
        {scope.kind !== "workspace" && (
          <div className="flex items-center gap-2 border-b border-border/60 bg-secondary/40 px-4 py-2 text-xs text-muted-foreground">
            <Layers className="h-3.5 w-3.5" strokeWidth={2} />
            <span>
              Searching in <span className="font-medium text-foreground">{scopeLabel}</span>
            </span>
          </div>
        )}

        {/* Big search input */}
        <div className="flex h-14 items-center gap-3 border-b border-border px-4">
          <Search className="h-5 w-5 shrink-0 text-muted-foreground" strokeWidth={1.75} />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              scope.kind === "workspace"
                ? "Search for projects, files, and more"
                : `Search ${scopeSubject}`
            }
            className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
          {hasQuery ? (
            <button
              type="button"
              aria-label="Clear"
              onClick={() => {
                setQuery("");
                inputRef.current?.focus();
              }}
              className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              <X className="h-4 w-4" strokeWidth={2} />
            </button>
          ) : (
            <kbd className="hidden shrink-0 rounded border border-border bg-secondary/50 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground sm:inline-block">
              esc
            </kbd>
          )}
        </div>

        {/* Results / hint area */}
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {!hasQuery && <EmptyHint scope={scope} />}

          {hasQuery && (
            <>
              {/* "Show all results" — workspace scope only. Scoped searches
                  don't have a corresponding full-page view yet. */}
              {scope.kind === "workspace" && (
                <button
                  type="button"
                  onClick={goToSearchPage}
                  className="mb-1 flex w-full items-center justify-between rounded-lg border border-border/60 bg-secondary/40 px-3 py-2.5 text-left transition-colors hover:bg-secondary"
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <Search className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
                    <span className="truncate text-sm text-foreground">
                      Show all results for: <span className="font-medium">{query}</span>
                    </span>
                  </div>
                  <CornerDownLeft className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
                </button>
              )}

              {isLoading && totalCount === 0 && <LoadingRows />}
              {noResults && <NoResults query={query} scopeSubject={scopeSubject} />}

              {projects.length > 0 && (
                <Section title="Projects">
                  {projects.map((p) => (
                    <ProjectRow key={p.id} project={p} onClick={() => goToProject(p.id)} />
                  ))}
                </Section>
              )}

              {videos.length > 0 && (
                <Section title="Files">
                  {videos.map((v) => (
                    <VideoRow
                      key={v.id}
                      video={v}
                      projectName={projectNameById.get(v.projectId)}
                      onClick={() => goToVideo(v.id)}
                    />
                  ))}
                </Section>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border px-3 py-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <CornerDownLeft className="h-3 w-3" strokeWidth={2} />
            <span>to open</span>
          </div>
          {scope.kind === "workspace" && (
            <Link to={searchHref} onClick={onClose} className="hover:text-foreground">
              Advanced search
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Row components ──────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-2 first:mt-0">
      <div className="px-2 py-1 text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
        {title}
      </div>
      <div className="flex flex-col gap-0.5">{children}</div>
    </div>
  );
}

function ProjectRow({ project, onClick }: { project: Project; onClick: () => void }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-secondary"
    >
      <div className="grid h-10 w-14 shrink-0 place-items-center overflow-hidden rounded-md bg-secondary/60">
        <FolderKanban className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-foreground">{project.name}</div>
        <div className="truncate text-xs text-muted-foreground">Project</div>
      </div>
    </button>
  );
}

function VideoRow({
  video,
  projectName,
  onClick,
}: {
  video: Video;
  projectName?: string;
  onClick: () => void;
}) {
  const duration = formatDurationChip(video.durationSeconds, video.assetType);
  const typeLabel =
    video.assetType === "audio" ? "Audio" : video.assetType === "image" ? "Image" : "Video";
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-secondary"
    >
      <div className="relative grid h-10 w-14 shrink-0 place-items-center overflow-hidden rounded-md bg-secondary/60">
        {video.assetType === "video" && video.cdnUrl ? (
          <video src={video.cdnUrl} muted preload="metadata" className="h-full w-full object-cover" />
        ) : video.assetType === "image" && video.cdnUrl ? (
          <img src={video.cdnUrl} alt={video.title} loading="lazy" className="h-full w-full object-cover" />
        ) : video.assetType === "audio" ? (
          <Music2 className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
        ) : video.assetType === "image" ? (
          <FileImage className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
        ) : (
          <VideoIcon className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
        )}
        {duration && (
          <span className="pointer-events-none absolute bottom-0.5 right-0.5 rounded bg-black/70 px-1 text-[9px] font-medium tabular-nums text-white">
            {duration}
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-foreground">{video.title}</div>
        <div className="truncate text-xs text-muted-foreground">
          {typeLabel}
          {projectName && (
            <>
              <span className="mx-1.5">·</span>
              {projectName}
            </>
          )}
        </div>
      </div>
    </button>
  );
}

// ── States ──────────────────────────────────────────────────────────────────

function EmptyHint({ scope }: { scope: SearchScope }) {
  const subject =
    scope.kind === "workspace"
      ? "your workspace"
      : scope.kind === "project"
      ? "this project"
      : "this folder";
  return (
    <div className="flex flex-col items-center gap-2 px-3 py-10 text-center">
      <div className="grid h-10 w-10 place-items-center rounded-xl bg-secondary">
        <Search className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
      </div>
      <div className="text-sm font-medium text-foreground">Search {subject}</div>
      <p className="max-w-xs text-xs text-muted-foreground">
        {scope.kind === "workspace"
          ? "Type a project or file name to get started."
          : "Type a file name to get started."}
      </p>
    </div>
  );
}

function LoadingRows() {
  return (
    <div className="flex flex-col gap-1 px-1 py-1">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 rounded-lg px-3 py-2">
          <div className="h-10 w-14 shrink-0 animate-pulse rounded-md bg-secondary/60" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="h-3 w-2/3 animate-pulse rounded bg-secondary/60" />
            <div className="h-2.5 w-1/3 animate-pulse rounded bg-secondary/40" />
          </div>
        </div>
      ))}
    </div>
  );
}

function NoResults({ query, scopeSubject }: { query: string; scopeSubject: string }) {
  return (
    <div className="flex flex-col items-center gap-1 px-3 py-6 text-center">
      <div className="text-sm font-medium text-foreground">
        No matches for "{query}" in {scopeSubject}
      </div>
      <p className="text-xs text-muted-foreground">Try a different word or check for typos.</p>
    </div>
  );
}
